// @flow

import { Component, type Node } from 'react';
import ReactDOM from 'react-dom';
import { connect } from 'react-redux';
import type { Item } from 'app/common/react/components/Masonry/types';
import type { State as Pins } from 'app/common/redux/reducers/pins';
import type { State as ReduxState } from 'app/common/redux/types';
import {
  getMeasurementCache,
  getMinColumnHeight,
} from 'app/common/react/components/Masonry/measurementCache';
import { stopTiming, logCustomTraceEvent } from 'app/common/lib/TraceLogger/logTraceEvent';

export type ProfilerInput = {|
  cacheKey?: ?string,
  isFetching: boolean,
  items: Array<Object>,
  mapPinUrlsToItems?: (state: Pins, items: Array<Item>) => Array<Item>,
  useMasonry9?: boolean,
|};

type OwnProps = {|
  children: (measurementStore: Object) => Node,
  ...ProfilerInput,
|};

type ReduxStateProps = {|
  itemsNormalizedTracing: ?Array<Item>,
|};

type Props = {|
  ...OwnProps,
  ...ReduxStateProps,
|};

// This component measures above-the-fold visually complete of Masonry
class MasonryVisuallyCompleteProfiler extends Component<Props> {
  constructor(props: Props) {
    super(props);
    logCustomTraceEvent({ name: 'MasonryConstructor' });
  }

  componentDidMount() {
    logCustomTraceEvent({ name: 'MasonryDidMount' });

    // Check if the viewport will be filled by the first page of pins
    // If not, then wait for the next page of pins to load and check again
    // using the bounding box instead of the viewport since the user may have
    // scrolled at this point
    const screenHeight = window.innerHeight;
    // Grab the first pin by querying for the first image that has a srcset property
    const firstPin = document.querySelector('img[srcset]');
    if (firstPin) {
      const topOffset = firstPin.getBoundingClientRect() && firstPin.getBoundingClientRect().top;
      // The height of the grid visible in the viewport
      this.gridVisibleHeight = screenHeight && screenHeight - topOffset;
    }

    this.handleChanges('componentDidMount');
  }

  componentDidUpdate() {
    this.handleChanges('componentDidUpdate');
  }

  gridVisibleHeight: ?number = null;

  handleChanges = (type: 'componentDidMount' | 'componentDidUpdate') => {
    const viewportFilled = this.checkViewportFilled();
    if (!this.props.isFetching) {
      logCustomTraceEvent({
        name: 'UserNavRegisterGridImagesTraceEvent',
        viewportFilled,
        gridData: this.props.itemsNormalizedTracing || this.props.items,
        el: ReactDOM.findDOMNode(this),
      });
      logCustomTraceEvent({ name: 'UserNavRegisterGridItemsPositionedTraceEvent' });
      stopTiming(PCONST.thrift.TracingClientTypes.PWTAction.SEARCH_FEED_RENDER);
      stopTiming(PCONST.thrift.TracingClientTypes.PWTAction.HOME_FEED_RENDER);
      // Stop board render timing (large screens that required more grid item fetching)
      stopTiming(PCONST.thrift.TracingClientTypes.PWTAction.BOARD_PAGE_LOAD);
    }

    if (type === 'componentDidMount' || viewportFilled) {
      logCustomTraceEvent({
        name: 'PageLoadRegisterGridImagesTraceEvent',
        viewportFilled,
        gridData: this.props.itemsNormalizedTracing || this.props.items,
        el: ReactDOM.findDOMNode(this),
      });
    }
  };

  checkViewportFilled(): boolean {
    const { gridVisibleHeight } = this;
    const { cacheKey, useMasonry9 } = this.props;
    return !!gridVisibleHeight && getMinColumnHeight({ cacheKey, useMasonry9 }) > gridVisibleHeight;
  }

  render() {
    const { children, cacheKey, useMasonry9 } = this.props;
    return children(getMeasurementCache(cacheKey, useMasonry9));
  }
}

const mapStateToProps = (
  { pins }: ReduxState,
  { mapPinUrlsToItems, items }: OwnProps,
): ReduxStateProps => ({
  itemsNormalizedTracing: mapPinUrlsToItems ? mapPinUrlsToItems(pins, items) : null,
});

export default connect<Props, OwnProps, _, _, _, _>(
  mapStateToProps,
  () => ({}), // opt-out from injecting dispatch prop
)(MasonryVisuallyCompleteProfiler);
